/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { MenuItem, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { tfiId, UserType, Paths } from '../../../../Shared/Constants/App';
import { buttonStyles } from './AddEditOneTimeJob.Style';
import GenericAutocompleteWithAdd from './GenericAutocompleteWithAdd';

const TfiLocationAutocomplete = ({
  role,
  selectedCustomer,
  tfiFacilities,
  selectedFacilityId,
  handleFacilityChange,
  isLoading,
  searchString,
  setSearchString,
  totalCounts,
  currentPages,
  handlePaginationChanges,
}: any): JSX.Element | null => {
  const { t } = useTranslation(['oneTimeJob', 'homePage']);
  const navigate = useNavigate();
  const theme = useTheme();
  const { textField } = buttonStyles;

  if (role !== UserType.Vendor || selectedCustomer?.customerId !== tfiId) {
    return null;
  }

  const redirectPath = Paths.AddFacility;
  const selectedOption = tfiFacilities?.find((facility: any) => facility.facilityId === selectedFacilityId) || null;

  const renderOption = (props: any, option: any) => {
    const isSelected = option?.facilityId === selectedFacilityId;
    return (
      <MenuItem
        {...props}
        selected={isSelected}
        sx={{
          borderBottom: `0.0625rem solid ${theme.palette.divider}`,
          backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.common.white,
          fontWeight: isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightLight,
          color: isSelected ? theme.palette.common.black : 'inherit',
          '&:hover': {
            backgroundColor: isSelected ? theme.palette.primary.dark : `${theme.palette.secondary.dark} !important`,
          },
        }}>
        {option.buildingName}
      </MenuItem>
    );
  };

  return (
    <GenericAutocompleteWithAdd
      options={tfiFacilities || []}
      getOptionLabel={(option) => option?.buildingName || ''}
      value={selectedOption}
      onChange={(event, newValue) => handleFacilityChange(newValue?.facilityId)}
      isOptionEqualToValue={(option, value) => option.facilityId === value?.facilityId}
      label={
        <span>
          {t('dashboard:location')}
          <span className="mandatory-field">*</span>
        </span>
      }
      loading={isLoading}
      showAddButton={true}
      onAddClick={() =>
        navigate(redirectPath, {
          state: {
            isFromJob: true,
            customerId: selectedCustomer?.customerId,
          },
        })
      }
      addButtonLabel={t('homePage:addFacility')}
      renderOption={renderOption}
      textFieldSx={{
        ...textField,
        marginBottom: '2rem',
      }}
      searchString={searchString}
      setSearchString={setSearchString}
      totalCounts={totalCounts}
      currentPages={currentPages}
      handlePaginationChanges={handlePaginationChanges}
    />
  );
};

export default TfiLocationAutocomplete;
